// CardSettings.cshtml: activate, lock / unlock (the processors' "freeze"; the cardholder reads "lock": W22), report
// lost (reissue). Two things the legacy page did not do (W11):
// a processor that has no unfreeze (InterCash) says so before the freeze and offers no Unfreeze after it; and after a
// reissue the session is read again, because the replacement is another card with another reference.
// Added where the card's processor offers them (Central Payments): its PIN page, and its image of the card — both
// hosted by the processor, opened from a short-lived link; the card number never passes through QOR's answers.
import { refreshSession, useSetSession } from '@qor/web-shared';
import { useState } from 'react';
import { cardApi, type CardAction } from '../api';
import { isFrozen, needsActivation, NO_UNFREEZE } from '../account';
import { Alert, NO_OUTCOME, type Outcome } from '../components/Alert';
import { CardPage, type CardState } from '../components/CardPage';
import { usePageTitle } from '../components/CustomerLayout';
import { useUser } from '../components/RequireAuth';

export const ACTION_DONE: Record<CustomerCardAction, string> = {
  activate: 'Your card is now active.',
  freeze: 'Your card is locked. No new purchases will go through.',
  unfreeze: 'Your card is unlocked and ready to use.',
  reissue: 'A replacement card is on the way.',
};

/** Converting to a physical card is the console's only. */
type CustomerCardAction = Exclude<CardAction, 'convert-to-physical'>;

const REISSUE_CONFIRM = 'This will close your current card and order a replacement. Continue?';

export const FREEZE_FOR_GOOD_CONFIRM = 'A locked card can only be unlocked by customer support. Lock this card?';

export function CardSettingsPage() {
  usePageTitle('Card Settings');
  return <CardPage feature="CardSettings">{(card) => <CardSettingsView card={card} />}</CardPage>;
}

function CardSettingsView({ card }: { card: CardState }) {
  const [outcome, setOutcome] = useState<Outcome>(NO_OUTCOME);
  const [busy, setBusy] = useState(false);
  const [pinLink, setPinLink] = useState<string | null>(null);
  const [cardImage, setCardImage] = useState<string | null>(null);
  const user = useUser();
  const { capabilities } = card.account;
  const setSession = useSetSession();
  const frozen = isFrozen(card.account);
  const noUnfreeze = NO_UNFREEZE.has(user.provider);

  async function run(action: CustomerCardAction) {
    const cardReferenceId = card.account.cardReferenceId;
    if (!cardReferenceId) return;
    if (action === 'reissue' && !window.confirm(REISSUE_CONFIRM)) return;
    if (action === 'freeze' && noUnfreeze && !window.confirm(FREEZE_FOR_GOOD_CONFIRM)) return;
    setBusy(true);
    try {
      const result = await cardApi.cardAction(card.reference, action, cardReferenceId);
      setOutcome({ ok: result.success, message: result.success ? ACTION_DONE[action] : result.message });
      await card.refresh();
      if (action === 'reissue' && result.success) setSession(await refreshSession());
    } finally {
      setBusy(false);
    }
  }

  async function link(kind: 'pin' | 'image') {
    setBusy(true);
    try {
      const r = kind === 'pin' ? await cardApi.pinWidget(card.reference) : await cardApi.cardImage(card.reference);
      if (!r.success || !('url' in r)) return setOutcome({ ok: false, message: r.message });
      setOutcome(NO_OUTCOME);
      if (kind === 'pin') setPinLink(r.url);
      else setCardImage(r.url);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1 className="qcw-greeting">Card settings</h1>
      <p className="qcw-subgreeting">Control your card&apos;s security in real time.</p>

      <Alert {...outcome} />

      <section className="qcw-section" aria-label="Card settings">
        {needsActivation(card.account) ? (
          <div className="qcw-section-body">
            <p className="qcw-section-hint">Your card has been issued and is ready to activate.</p>
            <button type="button" className="qcw-btn-primary" disabled={busy} onClick={() => void run('activate')}>
              Activate card
            </button>
          </div>
        ) : (
          <>
            <div className="qcw-control-row">
              <div>
                <div className="qcw-control-label">{frozen ? 'Card locked' : 'Lock card'}</div>
                <p className="qcw-control-hint">
                  {noUnfreeze
                    ? frozen
                      ? 'No new purchases will go through. Call customer support to unlock this card.'
                      : 'Temporarily block new purchases. Call customer support to unlock anytime.'
                    : frozen
                      ? 'No new purchases will go through until you unlock it.'
                      : 'Temporarily block new purchases. You can unlock any time.'}
                </p>
              </div>
              {!(frozen && noUnfreeze) && (
                <button
                  type="button"
                  className="qcw-btn-ghost"
                  disabled={busy}
                  onClick={() => void run(frozen ? 'unfreeze' : 'freeze')}
                >
                  {frozen ? 'Unlock' : 'Lock'}
                </button>
              )}
            </div>

            <div className="qcw-control-row">
              <div>
                <div className="qcw-control-label">Lost or stolen card</div>
                <p className="qcw-control-hint">Closes this card immediately and orders a replacement.</p>
              </div>
              <button type="button" className="qcw-btn-ghost" disabled={busy} onClick={() => void run('reissue')}>
                Report lost
              </button>
            </div>

            {capabilities.pinWidget && (
              <div className="qcw-control-row">
                <div>
                  <div className="qcw-control-label">Change PIN</div>
                  <p className="qcw-control-hint">
                    {pinLink
                      ? 'The secure PIN page is ready. The link works for a few minutes only.'
                      : "Set a new PIN on the card issuer's secure page."}
                  </p>
                </div>
                {pinLink ? (
                  <a className="qcw-btn-ghost" href={pinLink} target="_blank" rel="noopener noreferrer">
                    Open PIN page
                  </a>
                ) : (
                  <button type="button" className="qcw-btn-ghost" disabled={busy} onClick={() => void link('pin')}>
                    Change PIN
                  </button>
                )}
              </div>
            )}

            {capabilities.cardImage && (
              <div className="qcw-control-row">
                <div>
                  <div className="qcw-control-label">Card details</div>
                  <p className="qcw-control-hint">
                    See your card number to shop online. Shown by the card issuer, for a few minutes only.
                  </p>
                  {cardImage && (
                    <img
                      src={cardImage}
                      alt="Your card details"
                      referrerPolicy="no-referrer"
                      style={{ maxWidth: '100%', marginTop: 8, borderRadius: 12 }}
                    />
                  )}
                </div>
                {cardImage ? (
                  <button type="button" className="qcw-btn-ghost" onClick={() => setCardImage(null)}>
                    Hide
                  </button>
                ) : (
                  <button type="button" className="qcw-btn-ghost" disabled={busy} onClick={() => void link('image')}>
                    Show card details
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
